import proj4 from "proj4";
import * as THREE from "three";
/**
 * @class Spatial
 *
 * @classdesc Provides methods for scalar, vector and matrix calculations.
 */
export class Spatial {
    /**
     * Converts azimuthal phi rotation (counter-clockwise with origin on X-axis) to
     * bearing (clockwise with origin at north or Y-axis).
     *
     * @param {number} phi - Azimuthal phi angle in radians.
     * @returns {number} Bearing in radians.
     */
    static azimuthalToBearing(phi) {
        return -phi + Math.PI / 2;
    }
    /**
     * Converts degrees to radians.
     *
     * @param {number} deg - Degrees.
     * @returns {number} Radians.
     */
    static degToRad(deg) {
        return (Math.PI * deg) / 180;
    }
    /**
     * Converts radians to degrees.
     *
     * @param {number} rad - Radians.
     * @returns {number} Degrees.
     */
    static radToDeg(rad) {
        return (180 * rad) / Math.PI;
    }
    /**
     * Creates a rotation matrix from an angle-axis vector.
     *
     * @param {Array<number>} angleAxis - Angle-axis representation of a rotation.
     * @returns {THREE.Matrix4} Rotation matrix.
     */
    static rotationMatrix(angleAxis) {
        const axis = new THREE.Vector3(angleAxis[0], angleAxis[1], angleAxis[2]);
        const angle = axis.length();
        if (angle > 0) {
            axis.normalize();
        }
        return new THREE.Matrix4().makeRotationAxis(axis, angle);
    }
    /**
     * Rotates a vector according to a angle-axis rotation vector.
     *
     * @param {Array<number>} vector - Vector to rotate.
     * @param {Array<number>} angleAxis - Angle-axis representation of a rotation.
     * @returns {THREE.Vector3} Rotated vector.
     */
    static rotate(vector, angleAxis) {
        const v = new THREE.Vector3(vector[0], vector[1], vector[2]);
        const rotationMatrix = this.rotationMatrix(angleAxis);
        v.applyMatrix4(rotationMatrix);
        return v;
    }
    /**
     * Calculates the optical center from a rotation vector
     * on the angle-axis representation and a translation vector
     * according to C = -R^T t.
     *
     * @param {Array<number>} rotation - Angle-axis representation of a rotation.
     * @param {Array<number>} translation - Translation vector.
     * @returns {THREE.Vector3} Optical center.
     */
    static opticalCenter(rotation, translation) {
        const angleAxis = [-rotation[0], -rotation[1], -rotation[2]];
        const vector = [-translation[0], -translation[1], -translation[2]];
        return this.rotate(vector, angleAxis);
    }
    /**
     * Calculates the viewing direction from a rotation vector
     * on the angle-axis representation.
     *
     * @param {number[]} rotation - Angle-axis representation of a rotation.
     * @returns {THREE.Vector3} Viewing direction.
     */
    static viewingDirection(rotation) {
        const angleAxis = [-rotation[0], -rotation[1], -rotation[2]];
        return this.rotate([0, 0, 1], angleAxis);
    }
    /**
     * Wrap a number on the interval [min, max].
     *
     * @param {number} value - Value to wrap.
     * @param {number} min - Lower endpoint of interval.
     * @param {number} max - Upper endpoint of interval.
     * @returns {number} The wrapped number.
     */
    static wrap(value, min, max) {
        if (max < min) {
            throw new Error("Invalid arguments: max must be larger than min.");
        }
        const interval = max - min;
        while (value > max || value < min) {
            if (value > max) {
                value = value - interval;
            }
            else if (value < min) {
                value = value + interval;
            }
        }
        return value;
    }
    /**
     * Wrap an angle on the interval [-Pi, Pi].
     *
     * @param {number} angle - Value to wrap.
     * @returns {number} Wrapped angle.
     */
    static wrapAngle(angle) {
        return this.wrap(angle, -Math.PI, Math.PI);
    }
    /**
     * Limit the value to the interval [min, max] by changing the value to
     * the nearest available one when it is outside the interval.
     *
     * @param {number} value - Value to clamp.
     * @param {number} min - Minimum of the interval.
     * @param {number} max - Maximum of the interval.
     * @returns {number} Clamped value.
     */
    static clamp(value, min, max) {
        if (value < min) {
            return min;
        }
        if (value > max) {
            return max;
        }
        return value;
    }
    /**
     * Calculates the counter-clockwise angle from the first
     * vector (x1, y1)^T to the second (x2, y2)^T.
     *
     * @param {number} x1 - X coordinate of first vector.
     * @param {number} y1 - Y coordinate of first vector.
     * @param {number} x2 - X coordinate of second vector.
     * @param {number} y2 - Y coordinate of second vector.
     * @returns {number} Counter clockwise angle between the vectors.
     */
    static angleBetweenVector2(x1, y1, x2, y2) {
        const angle = Math.atan2(y2, x2) - Math.atan2(y1, x1);
        return this.wrapAngle(angle);
    }
    /**
     * Calculates the minimum (absolute) angle change for rotation
     * from one angle to another on the [-Pi, Pi] interval.
     *
     * @param {number} angle1 - Start angle.
     * @param {number} angle2 - Destination angle.
     * @returns {number} Absolute angle change between angles.
     */
    static angleDifference(angle1, angle2) {
        const angle = angle2 - angle1;
        return this.wrapAngle(angle);
    }
    /**
     * Calculates the relative rotation angle between two
     * angle-axis vectors.
     *
     * @param {number} rotation1 - First angle-axis vector.
     * @param {number} rotation2 - Second angle-axis vector.
     * @returns {number} Relative rotation angle.
     */
    static relativeRotationAngle(rotation1, rotation2) {
        const R1T = this.rotationMatrix([-rotation1[0], -rotation1[1], -rotation1[2]]);
        const R2 = this.rotationMatrix(rotation2);
        const R = R1T.multiply(R2);
        const elements = R.elements;
        // from Tr(R) = 1 + 2 * cos(theta)
        const tr = elements[0] + elements[5] + elements[10];
        const theta = Math.acos(Math.max(Math.min((tr - 1) / 2, 1), -1));
        return theta;
    }
    /**
     * Calculates the angle from a vector to a plane.
     *
     * @param {Array<number>} vector - The vector.
     * @param {Array<number>} planeNormal - Normal of the plane.
     * @returns {number} Angle from between plane and vector.
     */
    static angleToPlane(vector, planeNormal) {
        const v = new THREE.Vector3().fromArray(vector);
        const norm = v.length();
        const _epsilon = 1e-9;
        if (norm < _epsilon) {
            return 0;
        }
        const projection = v.dot(new THREE.Vector3().fromArray(planeNormal));
        return Math.asin(projection / norm);
    }
    static azimuthal(direction, up) {
        const directionVector = new THREE.Vector3().fromArray(direction);
        const upVector = new THREE.Vector3().fromArray(up);
        const upProjection = directionVector.clone().dot(upVector);
        const planeProjection = directionVector
            .clone()
            .sub(upVector.clone().multiplyScalar(upProjection));
        return Math.atan2(planeProjection.y, planeProjection.x);
    }
    /**
     * Calculates the distance between two coordinates
     * (latitude, longitude, and optional altitude) in meters
     * according to the haversine formula.
     *
     * @param {number} lat1 - Latitude of the first coordinate in degrees.
     * @param {number} lat2 - Latitude of the second coordinate in degrees.
     * @param {number} lon1 - Longitude of the first coordinate in degrees.
     * @param {number} lon2 - Longitude of the second coordinate in degrees.
     * @param {number} alt1 - Altitude of the first coordinate in meters (optional).
     * @param {number} alt2 - Altitude of the second coordinate in meters (optional).
     * @returns {number} Distance between lat lon positions in meters.
     */
    static distanceFromLatLonAlt(lat1, lat2, lon1, lon2, alt1 = 0, alt2 = 0) {
        const r = 6371000; // Earth's radius in meters
        const dLat = this.degToRad(lat2 - lat1);
        const dLon = this.degToRad(lon2 - lon1);
        const hav = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
            Math.cos(this.degToRad(lat1)) * Math.cos(this.degToRad(lat2)) * Math.sin(dLon / 2) * Math.sin(dLon / 2);
        const horizontalDistance = 2 * r * Math.atan2(Math.sqrt(hav), Math.sqrt(1 - hav));
        const verticalDistance = alt2 - alt1;
        return Math.sqrt(horizontalDistance ** 2 + verticalDistance ** 2);
    }
    /**
     * Calculates the rotation vector in angle axis representation
     * from euler angles in degrees.
     *
     * @param {number} x - Rotation about x-axis in degrees.
     * @param {number} y - Rotation about y-axis in degrees.
     * @param {number} z - Rotation about z-axis in degrees.
     * @param {string} order - Order in which to apply the rotations. Defaults to "ZYX".
     * @returns {number[]} Returns rotation vector in angle axis representation.
     */
    static rotationFromEulerAnglesDeg(x, y, z, order = "ZYX") {
        const euler = new THREE.Euler(this.degToRad(x), this.degToRad(y), this.degToRad(z), order);
        const rotation = new THREE.Vector4().setAxisAngleFromRotationMatrix(new THREE.Matrix4().makeRotationFromEuler(euler));
        return rotation.multiplyScalar(rotation.w).toArray().slice(0, 3);
    }
    static rotationDifference(rotation1, rotation2) {
        const R_a_b = this.rotationMatrix([rotation1[0], rotation1[1], rotation1[2]]);
        const R_a_c = this.rotationMatrix([rotation2[0], rotation2[1], rotation2[2]]);
        R_a_b.multiply(R_a_c.transpose());
        const rotation = new THREE.Vector4().setAxisAngleFromRotationMatrix(R_a_b);
        return rotation.multiplyScalar(rotation.w).toArray().slice(0, 3);
    }
    static rotationFromEulerAnglesDegAndVehicleOrientation(x, y, z, vehicleOrientation, order = "ZYX") {
        const c_rotation = this.rotationFromEulerAnglesDeg(x, y, z, order);
        const v_rotation = this.rotationFromEulerAnglesDeg(0, 0, vehicleOrientation, order);
        return this.rotationDifference(c_rotation, v_rotation);
    }
    static viewingDirectionFromEulerAnglesDeg(x, y, z, order = "XYZ") {
        const euler = new THREE.Euler(this.degToRad(-x), this.degToRad(-y), this.degToRad(-z), order);
        const R = new THREE.Matrix4().makeRotationFromEuler(euler);
        const direction = new THREE.Vector4(0, 0, 1).applyMatrix4(R);
        return direction.toArray().slice(0, 3);
    }
    static upDirectionFromEulerAnglesDeg(x, y, z, order = "XYZ") {
        const euler = new THREE.Euler(this.degToRad(-x), this.degToRad(-y), this.degToRad(-z), order);
        const R = new THREE.Matrix4().makeRotationFromEuler(euler);
        const up = new THREE.Vector4(0, -1, 0).applyMatrix4(R);
        return up.toArray().slice(0, 3);
    }
    static azimuthFromEulerAnglesDeg(x, y, z, order = "XYZ") {
        const direction = this.viewingDirectionFromEulerAnglesDeg(x, y, z, order);
        const up = this.upDirectionFromEulerAnglesDeg(x, y, z, order);
        return this.azimuthal(direction, up);
    }
    static angleToPlaneFromEulerAnglesDeg(x, y, z, order = "XYZ") {
        const direction = this.viewingDirectionFromEulerAnglesDeg(x, y, z, order);
        return this.angleToPlane(direction, [0, 0, 1]);
    }
    static _opk_to_matrix(o_deg, p_deg, k_deg) {
        const so = Math.sin(this.degToRad(o_deg));
        const sp = Math.sin(this.degToRad(p_deg));
        const sk = Math.sin(this.degToRad(k_deg));
        const co = Math.cos(this.degToRad(o_deg));
        const cp = Math.cos(this.degToRad(p_deg));
        const ck = Math.cos(this.degToRad(k_deg));
        return new THREE.Matrix3().set(cp * ck, -cp * sk, sp, co * sk + so * sp * ck, co * ck - so * sp * sk, -so * cp, so * sk - co * sp * ck, so * ck + co * sp * sk, co * cp);
    }
    static _matrix_to_opk(matrix) {
        let o, k;
        const p = Math.atan2(matrix.elements[6], (matrix.elements[7] ** 2 + matrix.elements[8] ** 2) ** 0.5);
        const cp = Math.cos(p);
        if (cp != 0.0) {
            o = Math.atan2(-matrix.elements[7], matrix.elements[8]);
            k = Math.atan2(-matrix.elements[3], matrix.elements[0]);
        }
        else {
            o = 0.0;
            k = Math.atan2(matrix.elements[4], matrix.elements[5]);
            if (p != Math.PI / 2) {
                k *= -1;
            }
        }
        return { omega: this.radToDeg(o), phi: this.radToDeg(p), kappa: this.radToDeg(k) };
    }
    static _rot_wgs84_metric(o, p, k, meridian_convergence) {
        const r_pose = this._opk_to_matrix(o, p, k);
        const r_meridian_convergence = this._opk_to_matrix(0, 0, meridian_convergence);
        return this._matrix_to_opk(new THREE.Matrix3().multiplyMatrices(r_meridian_convergence.transpose(), r_pose));
    }
    static rotFromWGS84(pos, rot, epsg) {
        const wgs84_coords = proj4(`EPSG:${epsg}`, "EPSG:4326", {
            x: pos.easting,
            y: pos.northing,
            z: pos.height,
        });
        const coords_plus = proj4("EPSG:4326", `EPSG:${epsg}`, {
            x: wgs84_coords.x,
            y: wgs84_coords.y + 1,
            z: wgs84_coords.z,
        });
        const meridian_convergence = this.radToDeg(Math.atan2(coords_plus.x - pos.easting, coords_plus.y - pos.northing));
        return this._rot_wgs84_metric(rot.omega, rot.phi, rot.kappa, -meridian_convergence);
    }
    static rotToWGS84(pos, rot, epsg) {
        const wgs84_coords = proj4(`EPSG:${epsg}`, "EPSG:4326", {
            x: pos.easting,
            y: pos.northing,
            z: pos.height,
        });
        const coords_plus = proj4("EPSG:4326", `EPSG:${epsg}`, {
            x: wgs84_coords.x,
            y: wgs84_coords.y + 1,
            z: wgs84_coords.z,
        });
        const meridian_convergence = this.radToDeg(Math.atan2(coords_plus.x - pos.easting, coords_plus.y - pos.northing));
        return this._rot_wgs84_metric(rot.omega, rot.phi, rot.kappa, meridian_convergence);
    }
}
//# sourceMappingURL=Spatial.js.map